/* eslint-disable no-nested-ternary */
import {
  CONFIG_POLYGON_HOTSPOT_DEFAULT,
  DEFAULT_CONFIG_INSTRUCTOR_HOTSPOT,
  DEFAULT_CONFIG_SCENE_PLUGIN,
} from '@containers/Home/constants';
import {
  useHLookatKrpano,
  useVLookatKrpano,
  useZoomKrpano,
} from '@containers/Home/global/TourStats';
import {
  I_CommentScene,
  I_Hotspot,
  I_NadirConfig,
  I_PolygonHotspotConfigGlobal,
  I_Scene,
  I_SettingControlMode,
} from '@containers/Home/types';
import { separateColorAndTransparency } from '@utils/helper';
// import Image from '@assets/img/Rectangle.png';
import { useCallback, useMemo } from 'react';

interface Props {
  scene?: I_Scene;
  krpanoId: string;
}

export const useInternalFunction = ({ scene, krpanoId }: Props) => {
  const _window = window as any;
  const [, setZoomKrpano] = useZoomKrpano('zoom');
  const [, setHLookatKrpano] = useHLookatKrpano('hlookat');
  const [, setVLookatKrpano] = useVLookatKrpano('vlookat');

  const sceneChilds = useMemo(
    () =>
      (
        (scene?.mode === 'staging'
          ? scene?.staging
          : scene?.mode === 'day2dust'
          ? scene?.day2dust
          : []) || []
      ).map(item => ({
        ...item,
        xml: item?.media?.krpano_xml || '',
        urlTour:
          item?.media?.src_preview ||
          item?.media?.src_thumb ||
          item?.media?.src,
      })),
    [scene],
  );

  const onTriggerChangeView = useCallback(() => {
    _window[krpanoId].call(
      `mod(cur_hlookat,${_window[krpanoId].get('view.hlookat')},360)`,
    );
    _window[krpanoId].call(
      `mod(cur_Vlookat,${_window[krpanoId].get('view.vlookat')},360)`,
    );

    setHLookatKrpano(+_window[krpanoId].get('cur_hlookat'));
    setVLookatKrpano(+_window[krpanoId].get('cur_Vlookat'));
    _window[krpanoId].call('update_hotspot_visiblity()');
  }, [_window, krpanoId, setHLookatKrpano, setVLookatKrpano]);

  const onTriggerChangeZoom = useCallback(() => {
    setZoomKrpano(+_window[krpanoId].get('view.fov'));
  }, [_window, krpanoId, setZoomKrpano]);

  const onTriggerLoadCompleted = useCallback(
    (
      isTurnOffFollowMode: boolean,
      controlMode: I_SettingControlMode['mode'] = 'drag',
    ) => {
      if (scene?.media.subType !== 'flat')
        _window[krpanoId].set('view.limitview', 'range');

      setHLookatKrpano(
        scene?.config?.default_view?.x ?? _window[krpanoId].get('view.hlookat'),
      );
      setVLookatKrpano(
        scene?.config?.default_view?.y ?? _window[krpanoId].get('view.vlookat'),
      );
      setZoomKrpano(
        scene?.config?.default_view?.zoom_lv ??
          _window[krpanoId].get('view.fov'),
      );

      _window[krpanoId].set(
        'showerrors',
        process.env.NODE_ENV === 'development',
      );
      _window[krpanoId].set('logkey', process.env.NODE_ENV === 'development');
      _window[krpanoId].set(
        'debugmode',
        process.env.NODE_ENV === 'development',
      );

      if (!isTurnOffFollowMode) _window[krpanoId].call(`followmouse_enable()`);
      else _window[krpanoId].call(`followmouse_disable()`);

      if (controlMode && controlMode !== 'drag') {
        _window[krpanoId].set('control.mode', controlMode);
        // _window[krpanoId].set('control.touch', controlMode);
      }
    },
    [
      scene,
      _window,
      krpanoId,
      setZoomKrpano,
      setHLookatKrpano,
      setVLookatKrpano,
    ],
  );

  const onApplyConfigPluginKrpanoForScene = useCallback(() => {
    Object.entries(DEFAULT_CONFIG_SCENE_PLUGIN).forEach(
      ([keyParent, valueParent]) => {
        Object.entries(valueParent).forEach(([keyChild, valueChild]) => {
          const value =
            scene &&
            scene?.configPlugin &&
            scene?.configPlugin[keyParent] &&
            scene?.configPlugin[keyParent][keyChild]
              ? scene?.configPlugin[keyParent][keyChild]
              : valueChild;
          _window[krpanoId].set(`plugin[${keyParent}].${keyChild}`, value);
        });
      },
    );
  }, [_window, krpanoId, scene]);

  /**
   * ! SET DEFAULT VIEW, LIMIT VIEW, LIMIT ZOOM
   */
  const onRenderXmlKrpano = useCallback(
    (isLittlePlanetView = false) => {
      const transition =
        scene?.config?.typeTransition === '3d'
          ? 'OPENBLEND(0.5, 0.0, 0.75, 0.05, linear)'
          : scene?.config?.typeTransition;

      let view = '';

      const fovDefault = scene?.config?.isLittlePlanet
        ? 170
        : scene?.config?.default_view?.zoom_lv ??
          (isLittlePlanetView ? 170 : 90);

      if (scene?.media?.subType !== 'flat') {
        view += '<view ';
        if (isLittlePlanetView || scene?.config?.isLittlePlanet) {
          view += `fov='${
            !isLittlePlanetView && scene?.config?.isLittlePlanet
              ? 170
              : fovDefault
          }' fovmin='1' fovmax='179' hlookatmin='-180' hlookatmax='180' vlookatmin='-90' vlookatmax='90' vlookat='${
            !isLittlePlanetView && scene?.config?.isLittlePlanet
              ? 90
              : scene?.config?.default_view?.y ?? 90
          }' hlookat='${
            !isLittlePlanetView && scene?.config?.isLittlePlanet
              ? -60
              : scene?.config?.default_view?.x ?? -60
          }' stereographic='true' fisheye='1.0' fovtype='VFOV' limitview='lookat' /> <display flash10='off' detail='24' />`;
        } else {
          if (scene?.config?.default_view?.x !== undefined)
            view += ` hlookat="${scene?.config?.default_view?.x}"`;
          if (scene?.config?.default_view?.y !== undefined)
            view += ` vlookat="${scene?.config?.default_view?.y}"`;
          if (scene?.config?.default_view?.zoom_lv !== undefined)
            view += ` fov="${
              scene?.config?.typeTransition === '3d'
                ? fovDefault + 30
                : fovDefault
            }"`;
          view += ` fovmin="${scene?.config?.zoom_limit?.min ?? 60.0}"`;
          view += ` fovmax="
                ${scene?.config?.zoom_limit?.max ?? 150.0}"`;
          view += ' limitview="range"';
          view += ` hlookatmin="${scene?.config?.limit_view?.left ?? -180}"`;
          view += ` hlookatmax="${scene?.config?.limit_view?.right ?? 180}"`;
          view += ` vlookatmin="${scene?.config?.limit_view?.bottom ?? 90}"`;
          view += ` vlookatmax="${scene?.config?.limit_view?.top ?? -90}"`;
          view += ' />';
        }

        view += `${
          scene?.configPlugin?.lensflare?.enabled &&
          scene?.configPlugin?.lensflare?.ath &&
          scene?.configPlugin?.lensflare?.atv
            ? `<include url="%$mypath%/core/skin/lensflare.xml" /> <lensflare_settings editor="false" ath="${
                scene?.configPlugin?.lensflare?.ath ??
                DEFAULT_CONFIG_SCENE_PLUGIN.lensflare?.ath
              }" atv="${
                scene?.configPlugin?.lensflare?.atv ??
                DEFAULT_CONFIG_SCENE_PLUGIN.lensflare?.atv
              }" sun="${
                scene?.configPlugin?.lensflare?.sun ??
                DEFAULT_CONFIG_SCENE_PLUGIN.lensflare?.sun
              }" glare="${
                scene?.configPlugin?.lensflare?.glare ??
                DEFAULT_CONFIG_SCENE_PLUGIN.lensflare?.glare
              }" r_ring="${
                scene?.configPlugin?.lensflare?.r_ring ??
                DEFAULT_CONFIG_SCENE_PLUGIN.lensflare?.r_ring
              }" exposure="true" exposure_bias="${
                scene?.configPlugin?.lensflare?.exposure_bias ??
                DEFAULT_CONFIG_SCENE_PLUGIN.lensflare?.exposure_bias
              }"/>
                  <plugin name="pp_light" devices="html5" keep="false" url="%$mypath%/core/plugins/pp_light.js" enabled="true" exposure="0.0" lights="0.0" shadows="0.0" filterrange="60.0" masking="1.0" quality="7" order="" phase="2" />`
            : ''
        }`;
      }
      return { view, transition, fovDefault };
    },
    [scene],
  );

  /**
   * @param {hotspot}: info hotspot
   * @method {trigger sound play (hotspot instructor / hotspot sound)}
   * @method {add hotspot}
   */

  const onTriggerSoundHotspot = useCallback(
    (hotspot: I_Hotspot, isAllowTriggerSound: boolean) => {
      if (_window[krpanoId] && isAllowTriggerSound) {
        const name = `hotspot-${hotspot.id}-${krpanoId}-type${hotspot.type}-${
          hotspot.polygon ? 'ispolygon' : 'unpolygon'
        }`;
        if (hotspot.type === 'sound') {
          const xml = `sound1click(${name}, ${
            hotspot?.config?.sound?.file?.src || hotspot?.config?.sound?.url
          }, ${hotspot?.config?.sound?.action || 'pause'}, ${
            hotspot.config?.sound?.volume
              ? (hotspot.config.sound.volume / 100).toFixed(1)
              : 0.4
          }, ${
            hotspot?.config?.sound?.reach
              ? hotspot.config.sound.reach.toFixed(1)
              : 240
          }, ${hotspot?.config?.sound?.auto_play || false}, ${
            hotspot?.config?.sound?.loop || false
          })`;
          _window[krpanoId].call(xml);
        } else if (hotspot.type === 'instructor') {
          _window[krpanoId].call(`hotspot[${name}].togglepause()`);
          _window[krpanoId].set(
            `hotspot['${name}'].muted`,
            !isAllowTriggerSound,
          );
        }
      }
    },
    [_window, krpanoId],
  );

  const onAddHotspotNadirKrpano = useCallback(
    (nadirConfig: I_NadirConfig) => {
      const krpano = _window[krpanoId];

      const name = 'nadirlogo';
      krpano.set(`hotspot['${name}'].ath`, +0);
      krpano.set(`hotspot['${name}'].atv`, +90);
      krpano.set(`hotspot['${name}'].zoom`, false);
      krpano.set(`hotspot['${name}'].rotate`, 0);
      krpano.set(`hotspot['${name}'].scale`, nadirConfig?.scale || 1);
      krpano.set(`hotspot['${name}'].rotatewithview`, 'true');
      krpano.set(`hotspot['${name}'].distorted`, true);
      krpano.set(`hotspot['${name}'].url`, nadirConfig?.src || '');
      krpano.set(
        `hotspot['${name}'].visible`,
        !!(nadirConfig?.isEnabled && nadirConfig?.src),
      );
      if (nadirConfig?.link)
        krpano.set(`hotspot['${name}'].onclick`, () => {
          window.open(nadirConfig?.link, '_blank');
        });

      if (nadirConfig?.isEnabled && nadirConfig?.src)
        krpano.call(`addhotspot('${name}')`);
    },
    [_window, krpanoId],
  );

  const onBackNormalViewFromLittlePlanetView = useCallback(() => {
    setTimeout(() => {
      _window[krpanoId].call(
        `normalview(${scene?.config?.default_view?.x || 0}, ${
          scene?.config?.default_view?.y || 0
        }, ${scene?.config?.default_view?.zoom_lv || 90}, ${scene?.config
          ?.timeTransitionLittlePlanet})`,
      );
      setTimeout(
        () => {
          _window[krpanoId].set(
            'view.fovmin',
            scene?.config?.zoom_limit?.min ?? 60.0,
          );
          _window[krpanoId].set(
            'view.fovmax',
            scene?.config?.zoom_limit?.max ?? 150.0,
          );
          _window[krpanoId].set(
            'view.hlookatmin',
            scene?.config?.limit_view?.left ?? -180,
          );
          _window[krpanoId].set(
            'view.hlookatmax',
            scene?.config?.limit_view?.right ?? 180,
          );
          _window[krpanoId].set(
            'view.vlookatmin',
            scene?.config?.limit_view?.bottom ?? 90,
          );
          _window[krpanoId].set(
            'view.vlookatmax',
            scene?.config?.limit_view?.top ?? -90,
          );
          _window[krpanoId].call(
            `tween(view.tx|view.ty|view.tz|view.ox|view.oy|view.oz|view.fisheye, calc(''+image.ox+'|'+image.oy+'|'+image.oz+'|0|0|0|0'), 0.1, default)`,
          );
        },
        (scene?.config?.timeTransitionLittlePlanet || 2) * 1000 + 100,
      );
    }, 200);
  }, [_window, krpanoId, scene]);

  const onAddHotspotVRKrpanoUtils = useCallback(
    (hotspot: I_Hotspot) => {
      const krpano = _window[krpanoId];
      const name = `hotspot-${hotspot.id}-${krpanoId}-type${hotspot.type}-${
        hotspot.polygon ? 'ispolygon' : 'unpolygon'
      }-vrmode`;
      krpano.set(`hotspot['${name}'].name`, name);
      krpano.set(
        `hotspot['${name}'].url`,

        hotspot?.config?.style?.type === 'image' &&
          hotspot?.config?.style?.image?.src
          ? hotspot?.config?.style?.image?.src
          : hotspot.type === 'point'
          ? `${process.env.NEXT_PUBLIC_ASSETS_URL}/static/point.png`
          : hotspot.type === 'image'
          ? `${process.env.NEXT_PUBLIC_ASSETS_URL}/static/image.png`
          : hotspot.type === 'sound'
          ? `${process.env.NEXT_PUBLIC_ASSETS_URL}/static/sound.png`
          : `${process.env.NEXT_PUBLIC_ASSETS_URL}/static/video.png`,
      );
      krpano.set(`hotspot['${name}'].renderer`, 'webgl');
      krpano.set(`hotspot['${name}'].width`, '32');
      krpano.set(`hotspot['${name}'].height`, '32');
      krpano.set(`hotspot['${name}'].imagewidth`, '24');
      krpano.set(`hotspot['${name}'].imageheight`, '24');
      krpano.set(`hotspot['${name}'].depth`, '1000');
      krpano.set(`hotspot['${name}'].scale`, 1);
      krpano.set(`hotspot['${name}'].ath`, +hotspot.ath);
      krpano.set(`hotspot['${name}'].atv`, +hotspot.atv);
      krpano.set(
        `hotspot['${name}'].onover`,
        'tween(scale,1.5,easeOutExpo,0.2);',
      );
      krpano.set(`hotspot['${name}'].onout`, 'tween(scale,1,easeOutExpo,0.2);');
      krpano.set(
        `hotspot['${name}'].visible`,
        krpano.get('webvr.isenabled') ?? false,
      );
      krpano.set(
        `hotspot['${name}'].enabled`,
        krpano.get('webvr.isenabled') ?? false,
      );
      krpano.set(`hotspot['${name}'].align`, 'center');
      krpano.set(`hotspot['${name}'].zoom`, true);

      const nameTitle = `hotspot-${hotspot.id}-${krpanoId}-type${
        hotspot.type
      }-${hotspot.polygon ? 'ispolygon' : 'unpolygon'}-title-vrmode`;
      krpano.set(`hotspot['${nameTitle}'].type`, 'text');
      krpano.set(`hotspot['${nameTitle}'].scale`, '0.1');
      krpano.set(`hotspot['${nameTitle}'].zoom`, true);
      krpano.set(`hotspot['${nameTitle}'].align`, 'center');
      krpano.set(`hotspot['${nameTitle}'].css`, 'font-size:12px;color:#ffffff');
      krpano.set(`hotspot['${nameTitle}'].html`, hotspot.title);
      krpano.set(`hotspot['${nameTitle}'].bg`, true);
      krpano.set(`hotspot['${nameTitle}'].bgcolor`, '#000000');
      krpano.set(`hotspot['${nameTitle}'].bgalpha`, '0.5');
      krpano.set(`hotspot['${nameTitle}'].edge`, 'center');
      krpano.set(`hotspot['${nameTitle}'].ath`, hotspot.ath);
      krpano.set(`hotspot['${nameTitle}'].atv`, hotspot.atv - 3);
      krpano.set(`hotspot['${nameTitle}'].zorder`, 4001);
      krpano.set(
        `hotspot['${nameTitle}'].visible`,
        krpano.get('webvr.isenabled') ?? false,
      );
      krpano.set(
        `hotspot['${nameTitle}'].enabled`,
        krpano.get('webvr.isenabled') ?? false,
      );
      krpano.set(`hotspot['${nameTitle}'].depth`, 100);

      return { krpano, name, nameTitle };
    },
    [_window, krpanoId],
  );

  const onAddHotspotKrpanoUtils = useCallback(
    (
      hotspot: I_Hotspot,
      keepHotspotPositionWhenZooming = false,
      patchConfig: any = undefined,
    ) => {
      const krpano = _window[krpanoId];
      const name = `hotspot-${hotspot.id}-${krpanoId}-type${hotspot.type}-${
        hotspot.polygon ? 'ispolygon' : 'unpolygon'
      }${patchConfig?.action ? `-${patchConfig?.action}` : ''}`;
      krpano.set(`hotspot['${name}'].name`, name);
      // krpano.set(`hotspot['${name}'].keep`, 'true');
      krpano.set(`hotspot['${name}'].zoom`, !!keepHotspotPositionWhenZooming);
      krpano.set(`hotspot['${name}'].styleType`, hotspot.config?.style?.type);
      krpano.set(
        `hotspot['${name}'].visible`,
        hotspot.type !== 'chevron'
          ? !krpano.get('webvr.isenabled') || true
          : true,
      );
      krpano.set(
        `hotspot['${name}'].enabled`,
        hotspot.type !== 'chevron'
          ? !krpano.get('webvr.isenabled') || true
          : true,
      );
      if (hotspot.type !== 'patch') {
        krpano.set(`hotspot['${name}'].onover`, () => {
          krpano.set(`hotspot['${name}'].zorder`, 4003);
        });
        krpano.set(`hotspot['${name}'].onout`, () => {
          krpano.set(`hotspot['${name}'].zorder`, 4002);
        });
      }

      function onConfigHotspotPolygon(
        polygonHotspotConfigGlobal: I_PolygonHotspotConfigGlobal,
      ) {
        krpano.set(`hotspot['${name}'].renderer`, 'webgl');
        const config = hotspot.polygon_config;
        const fillColor = separateColorAndTransparency(
          config?.properties?.fillColor ||
            polygonHotspotConfigGlobal?.fillColor ||
            CONFIG_POLYGON_HOTSPOT_DEFAULT.fillColor,
        );
        const fillColorHover = separateColorAndTransparency(
          config?.properties?.fillColorHover ||
            polygonHotspotConfigGlobal?.fillColorHover ||
            CONFIG_POLYGON_HOTSPOT_DEFAULT.fillColorHover,
        );
        const borderColor = separateColorAndTransparency(
          config?.properties?.borderColor ||
            polygonHotspotConfigGlobal?.borderColor ||
            CONFIG_POLYGON_HOTSPOT_DEFAULT.borderColor,
        );
        const borderColorHover = separateColorAndTransparency(
          config?.properties?.borderColorHover ||
            polygonHotspotConfigGlobal?.borderColorHover ||
            CONFIG_POLYGON_HOTSPOT_DEFAULT.borderColorHover,
        );
        krpano.set(
          `hotspot[${name}].fillcolor`,
          fillColor.color.replace('#', '0x'),
        );
        krpano.set(
          `hotspot[${name}].bordercolor`,
          borderColor.color.replace('#', '0x'),
        );
        krpano.set(
          `hotspot[${name}].borderwidth`,
          config?.properties?.borderWidth ||
            polygonHotspotConfigGlobal?.borderWidth ||
            3,
        );
        krpano.set(`hotspot[${name}].fillalpha`, fillColor.alpha);
        krpano.set(`hotspot[${name}].borderalpha`, borderColor.alpha);
        krpano.set(
          `hotspot[${name}].polyline`,
          config.properties?.drawType === 'line',
        );

        krpano.set(`hotspot[${name}].tooltip`, hotspot.title);
        if (config?.points?.length)
          for (let i = 0; i < config?.points.length; i += 1) {
            const coordinate = config?.points[i];
            krpano.set(`hotspot[${name}].point[${i}].ath`, coordinate.ath);
            krpano.set(`hotspot[${name}].point[${i}].atv`, coordinate.atv);
          }
        // krpano.set(`hotspot[${name}].zorder`, 1);
        krpano.set(`hotspot[${name}].onhover`, () => {
          krpano.call(
            `tween(hotspot[${name}].fillcolor, ${fillColorHover.color.replace(
              '#',
              '0x',
            )}, 0.2);
              tween(hotspot[${name}].fillalpha, ${fillColorHover.alpha}, 0.2);`,
          );
          krpano.call(
            `tween(hotspot[${name}].bordercolor, ${borderColorHover.color.replace(
              '#',
              '0x',
            )}, 0.2);
              tween(hotspot[${name}].borderalpha, ${
                borderColorHover.alpha
              }, 0.2);`,
          );
        });
        krpano.set(`hotspot[${name}].onout`, () => {
          krpano.call(
            `tween(hotspot[${name}].fillcolor, ${fillColor.color.replace(
              '#',
              '0x',
            )}, 0.2);
              tween(hotspot[${name}].fillalpha, ${fillColor.alpha}, 0.2);`,
          );
          krpano.call(
            `tween(hotspot[${name}].bordercolor, ${borderColor.color.replace(
              '#',
              '0x',
            )}, 0.2);
              tween(hotspot[${name}].borderalpha, ${borderColor.alpha}, 0.2);`,
          );
          krpano.call(
            `copy(layer[tooltip].html, );tween(layer[tooltip].alpha, 0.0, 0.25, default, set(layer[tooltip].visible,false), copy(layer[tooltip].x,mouse.stagex); copy(layer[tooltip].y,mouse.stagey); );`,
          );
        });

        krpano.call(`hotspot[${name}].loadstyle(poly_hotspot_text)`);
      }

      function onConfigHotspotMedia() {
        krpano.set(`hotspot['${name}'].distorted`, 'true');
        krpano.set(`hotspot['${name}'].rx`, '0');
        krpano.set(`hotspot['${name}'].ry`, '0');
        krpano.set(`hotspot['${name}'].rz`, '0');
        krpano.set(`hotspot['${name}'].cursor`, 'unset');
        krpano.set(`hotspot['${name}'].capture`, false);

        krpano.set(`hotspot['${name}'].usecontentsize`, 'true');
      }

      function onConfigHotspotInstructor(isAllowTriggerSound: boolean) {
        krpano.set(
          `hotspot['${name}'].url`,
          '%$mypath%/core/plugins/videoplayer.js',
        );
        krpano.set(
          `hotspot['${name}'].videourl`,
          hotspot.config?.instructor?.media?.src,
        );
        krpano.set(`hotspot['${name}'].renderer`, 'auto');
        krpano.set(`hotspot['${name}'].distorted`, true);
        krpano.set(`hotspot['${name}'].zoom`, false);
        krpano.set(`hotspot['${name}'].autoresume`, true);
        krpano.set(`hotspot['${name}'].muted`, !isAllowTriggerSound);
        if (!isAllowTriggerSound)
          krpano.set(`hotspot['${name}'].pausedonstart`, true);
        else
          krpano.set(
            `hotspot['${name}'].pausedonstart`,
            hotspot.config?.instructor?.paused_on_start ??
              DEFAULT_CONFIG_INSTRUCTOR_HOTSPOT.paused_on_start,
          );
        krpano.set(
          `hotspot['${name}'].loop`,
          hotspot.config?.instructor?.loop ??
            DEFAULT_CONFIG_INSTRUCTOR_HOTSPOT.loop,
        );
        krpano.set(
          `hotspot['${name}'].directionalsound`,
          hotspot.config?.instructor?.sound_360 ??
            DEFAULT_CONFIG_INSTRUCTOR_HOTSPOT.sound_360,
        );
        krpano.set(
          `hotspot['${name}'].volume`,
          hotspot.config?.instructor?.volume ??
            DEFAULT_CONFIG_INSTRUCTOR_HOTSPOT.volume,
        );
        const maskColor = separateColorAndTransparency(
          hotspot.config?.instructor?.mask ??
            DEFAULT_CONFIG_INSTRUCTOR_HOTSPOT.mask,
        );
        krpano.set(
          `hotspot[${name}].chromakey`,
          `${maskColor.color.replace('#', '0x')}|${
            hotspot.config?.instructor?.smoothing ??
            DEFAULT_CONFIG_INSTRUCTOR_HOTSPOT.smoothing
          }|${
            hotspot.config?.instructor?.threshold ??
            DEFAULT_CONFIG_INSTRUCTOR_HOTSPOT.threshold
          }`,
        );
        const width =
          hotspot.config?.instructor?.width ??
          DEFAULT_CONFIG_INSTRUCTOR_HOTSPOT.width;
        krpano.set(`hotspot['${name}'].width`, width);
        const height =
          hotspot.config?.instructor?.height ??
          DEFAULT_CONFIG_INSTRUCTOR_HOTSPOT.height;
        krpano.set(`hotspot['${name}'].height`, height);
      }

      return {
        name,
        krpano,
        onConfigHotspotMedia,
        onConfigHotspotPolygon,
        onConfigHotspotInstructor,
      };
    },
    [krpanoId, _window],
  );

  const onAddCommentSceneIconUtils = useCallback(
    (
      commentScene: Partial<I_CommentScene>,
      isCommentSceneTemporary = false,
    ) => {
      const krpano = _window[krpanoId];
      const name = `commentscene-${commentScene?.id}-${krpanoId}${
        isCommentSceneTemporary ? '-temporary' : ''
      }`;
      krpano.set(`hotspot['${name}'].name`, name);
      if (scene?.media.subType !== 'flat') {
        krpano.set(`hotspot['${name}'].capture`, false);
        krpano.set(`hotspot['${name}'].zoom`, true);
      }
      krpano.set(`hotspot['${name}'].onover`, () => {
        krpano.set(`hotspot['${name}'].zorder`, 4003);
      });
      krpano.set(`hotspot['${name}'].onout`, () => {
        krpano.set(`hotspot['${name}'].zorder`, 4002);
      });
      krpano.set(`hotspot['${name}'].ath`, +(commentScene?.ath ?? 0));
      krpano.set(`hotspot['${name}'].atv`, +(commentScene?.atv ?? 0));
      krpano.set(
        `hotspot['${name}'].url`,
        `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAAtJREFUCB1jYAACAAAFAAGNu5vzAAAAAElFTkSuQmCC`,
      );
      krpano.set(`hotspot['${name}'].renderer`, 'css3d');
      return { krpano, name };
    },
    [_window, scene, krpanoId],
  );

  return useMemo(
    () => ({
      sceneChilds,
      onRenderXmlKrpano,
      onTriggerChangeView,
      onTriggerChangeZoom,
      onTriggerSoundHotspot,
      onTriggerLoadCompleted,
      onAddHotspotNadirKrpano,
      onAddHotspotKrpanoUtils,
      onAddHotspotVRKrpanoUtils,
      onAddCommentSceneIconUtils,
      onBackNormalViewFromLittlePlanetView,
      onApplyConfigPluginKrpanoForScene,
    }),
    [
      sceneChilds,
      onRenderXmlKrpano,
      onTriggerChangeView,
      onTriggerChangeZoom,
      onTriggerSoundHotspot,
      onTriggerLoadCompleted,
      onAddHotspotNadirKrpano,
      onAddHotspotKrpanoUtils,
      onAddHotspotVRKrpanoUtils,
      onAddCommentSceneIconUtils,
      onBackNormalViewFromLittlePlanetView,
      onApplyConfigPluginKrpanoForScene,
    ],
  );
};
